import React from 'react';
import { Link } from 'react-router-dom';
import { LinkedinIcon, InstagramIcon } from 'lucide-react';
const MyStory = () => {
  return <div className="bg-gray-900 text-white" data-id="element-558">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-559">
        <div className="max-w-4xl mx-auto" data-id="element-560">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-561">
            <div className="md:col-span-2" data-id="element-562">
              <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight mb-8" data-id="element-563">
                My Story
              </h1>
              <div className="prose prose-lg prose-invert" data-id="element-564">
                <p className="text-lg text-gray-300" data-id="element-565">
                  I began my career as a network engineer, the kind of person
                  who found joy in fixing the "invisible things" that made
                  systems talk to each other. But somewhere between configuring
                  routers and chasing downtimes, I realized I was more
                  fascinated by why we were building something than how it
                  worked. That curiosity slowly turned me from an engineer who
                  built systems to a product geek who builds experiences.
                </p>
                <p className="text-lg text-gray-300 mt-4" data-id="element-566">
                  Today, I spend my 9–5 solving user problems and my 5–9 talking
                  about them — writing about product, tech, and now AI on
                  LinkedIn. I love connecting with people who see products the
                  way I do — as a mix of logic, empathy, and occasional chaos.
                  Whether it's a new feature idea, an AI trend, or just a "wait,
                  why do users even do that?" moment, I'm always up for a good
                  product conversation (and a cup of coffee to go with it).
                </p>
                <h2 className="text-2xl font-bold text-white mt-8 mb-4" data-id="element-567">
                  Community & Recognition
                </h2>
                <p className="text-lg text-gray-300" data-id="element-568">
                  I've been fortunate to be named a LinkedIn Top Voice 2024,
                  where I share thoughts on product, tech, and AI. I also hosted{' '}
                  <a href="https://www.linkedin.com/posts/bhavinisapra_theproductfolks-grabchai-product-activity-7228588525871321088-8Qw2?utm_source=share&utm_medium=member_desktop" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline" data-id="element-569">
                    TPF's GrabChai meetup
                  </a>{' '}
                  in Kuala Lumpur — bringing together passionate builders for
                  caffeine and conversations.
                </p>
              </div>
            </div>
            <div className="md:col-span-1 flex items-center justify-center" data-id="element-570">
              <div className="w-40 h-40 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-indigo-400 shadow-lg shadow-indigo-400/20" data-id="element-571">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/q5VgxF1KJycKyYFo9s6vQQ/1758896736330.jpg" alt="Bhavini Sapra" className="w-full h-full object-cover" data-id="element-572" />
              </div>
            </div>
          </div>
          <section className="mb-12" data-id="element-573">
            <h2 className="text-2xl font-bold text-white mb-6" data-id="element-574">Education</h2>
            <div className="flex flex-col md:flex-row gap-4" data-id="element-575">
              <div className="bg-gray-800 rounded-lg p-5 border border-gray-700 flex-1 hover:shadow-lg hover:shadow-indigo-900/20 transition-all transform hover:-translate-y-1" data-id="element-576">
                <h3 className="text-lg font-semibold text-indigo-400" data-id="element-577">PGDM</h3>
                <p className="text-gray-300" data-id="element-578">NMIMS Mumbai (2019-2021)</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-5 border border-gray-700 flex-1 hover:shadow-lg hover:shadow-indigo-900/20 transition-all transform hover:-translate-y-1" data-id="element-579">
                <h3 className="text-lg font-semibold text-indigo-400" data-id="element-580">
                  B.Tech
                </h3>
                <p className="text-gray-300" data-id="element-581">
                  Poornima College of Engineering (2013-2017)
                </p>
              </div>
            </div>
          </section>
          <section className="mb-12" data-id="element-582">
            <h2 className="text-2xl font-bold text-white mb-6" data-id="element-583">
              Career Trajectory
            </h2>
            <div className="space-y-8" data-id="element-584">
              <div className="border-l-4 border-indigo-500 pl-4" data-id="element-585">
                <div className="flex items-center mb-2" data-id="element-586">
                  <div className="w-10 h-10 mr-3 bg-white rounded-full p-1 flex items-center justify-center" data-id="element-587">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iomPfmn47NsZYPfRT31d3P/amway.png" alt="Amway logo" className="w-8 h-8 object-contain" data-id="element-588" />
                  </div>
                  <h3 className="text-xl font-semibold text-white" data-id="element-589">
                    Product Owner at Amway Malaysia Sdn Bhd (Aug 2022 - Present)
                  </h3>
                </div>
                <p className="text-gray-300" data-id="element-590">
                  Led product initiatives in the Subscription domain for Amway's
                  NextGen platform, driving strategic growth across SEA markets
                  and improving user acquisition and retention by optimizing
                  end-to-end experiences.
                </p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4" data-id="element-591">
                <div className="flex items-center mb-2" data-id="element-592">
                  <div className="w-10 h-10 mr-3 bg-white rounded-full p-1 flex items-center justify-center" data-id="element-593">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sdC3p6y1DHNaNVTZ5FHPE5/deloitte.png" alt="Deloitte logo" className="w-8 h-8 object-contain" data-id="element-594" />
                  </div>
                  <h3 className="text-xl font-semibold text-white" data-id="element-595">
                    Senior Business Analyst at Deloitte India (Jan 2022 - Aug
                    2022)
                  </h3>
                </div>
                <p className="text-gray-300" data-id="element-596">
                  Designed and delivered AI-driven analytics and automation
                  solutions for global FMCG and telecom clients, transforming
                  data into actionable insights that improved decision-making
                  accuracy by 65%.
                </p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4" data-id="element-597">
                <div className="flex items-center mb-2" data-id="element-598">
                  <div className="w-10 h-10 mr-3 bg-white rounded-full p-1 flex items-center justify-center" data-id="element-599">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3ihuGTsyWbtQedG3TJzY2p/ericsson.png" alt="Ericsson logo" className="w-8 h-8 object-contain" data-id="element-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-white" data-id="element-601">
                    Business Analyst at Ericsson India Global Services (May 2018
                    - Dec 2021)
                  </h3>
                </div>
                <p className="text-gray-300" data-id="element-602">
                  Engineered and deployed automated reporting systems that
                  reduced field engineers' 4G/5G installation time by 70%,
                  improving operational efficiency and on-ground responsiveness.
                </p>
              </div>
            </div>
          </section>
          <section className="mb-12" data-id="element-603">
            <h2 className="text-2xl font-bold text-white mb-6" data-id="element-604">
              How I See Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-605">
              <div className="group relative" data-id="element-606">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-full opacity-0 group-hover:opacity-100 transform scale-95 group-hover:scale-105 transition-all duration-300" data-id="element-607"></div>
                <div className="relative bg-gray-800 rounded-full p-6 shadow-lg border border-gray-700 aspect-square flex items-center justify-center text-center transform transition-all duration-300 group-hover:translate-y-[-8px] group-hover:shadow-indigo-500/30 group-hover:shadow-xl z-10" data-id="element-608">
                  <p className="text-gray-300 group-hover:text-white transition-colors duration-300" data-id="element-609">
                    I believe a great product starts with making sense to the
                    user — it should feel intuitive, purposeful, and seamlessly
                    fit into their life.
                  </p>
                </div>
              </div>
              <div className="group relative" data-id="element-610">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-full opacity-0 group-hover:opacity-100 transform scale-95 group-hover:scale-105 transition-all duration-300" data-id="element-611"></div>
                <div className="relative bg-gray-800 rounded-full p-6 shadow-lg border border-gray-700 aspect-square flex items-center justify-center text-center transform transition-all duration-300 group-hover:translate-y-[-8px] group-hover:shadow-indigo-500/30 group-hover:shadow-xl z-10" data-id="element-612">
                  <p className="text-gray-300 group-hover:text-white transition-colors duration-300" data-id="element-613">
                    Every feature must solve a real problem, not just add
                    functionality — products that don't fix pain points end up
                    creating new ones.
                  </p>
                </div>
              </div>
              <div className="group relative" data-id="element-614">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-full opacity-0 group-hover:opacity-100 transform scale-95 group-hover:scale-105 transition-all duration-300" data-id="element-615"></div>
                <div className="relative bg-gray-800 rounded-full p-6 shadow-lg border border-gray-700 aspect-square flex items-center justify-center text-center transform transition-all duration-300 group-hover:translate-y-[-8px] group-hover:shadow-indigo-500/30 group-hover:shadow-xl z-10" data-id="element-616">
                  <p className="text-gray-300 group-hover:text-white transition-colors duration-300" data-id="element-617">
                    A product isn't truly successful until it's valuable for
                    both the user and the business — solving for delight and
                    monetization in the same breath.
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="border-t border-gray-700 pt-10 mb-10 text-center" data-id="element-618">
            <h2 className="text-2xl font-bold text-white mb-6" data-id="element-619">
              Connect & Follow me
            </h2>
            <div className="flex justify-center gap-6" data-id="element-620">
              <a href="https://www.linkedin.com/in/bhavinisapra/" target="_blank" rel="noopener noreferrer" className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center text-indigo-400 hover:bg-indigo-900 hover:text-white transition-colors" data-id="element-621">
                <LinkedinIcon className="h-6 w-6" data-id="element-622" />
              </a>
              <a href="https://www.instagram.com/growthwithbhavini/" target="_blank" rel="noopener noreferrer" className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center text-indigo-400 hover:bg-indigo-900 hover:text-white transition-colors" data-id="element-623">
                <InstagramIcon className="h-6 w-6" data-id="element-624" />
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>;
};
export default MyStory;